import React, { Fragment, useEffect, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import LiveBottombar from "./Live/LiveBottomBar";
import RecordedBottomBar from "./Recorded/RecordedBottomBar";
import PropTypes from "prop-types";
import { useMemo } from "react";
import EventBus from "orion-components/SharedComponents/EventBus";
import { checkUserIntPermissionByFeedId } from "orion-components/Session/Selectors";
import { makeSelectComponentProp } from "../../../SharedComponents/ComponentState/redux/selectors";
import { updateComponentInstance } from "../../../SharedComponents/ComponentState/redux/actions";

const propTypes = {
	selectedVideoMode: PropTypes.string.isRequired,
	camera: PropTypes.object.isRequired,
	contextId: PropTypes.string,
	playbackStreamName: PropTypes.string,
	dialogContainerRef: PropTypes.shape({
		current: PropTypes.any
	}),
	instanceId: PropTypes.string,
	inDock: PropTypes.bool,
	isOnAlertProfile: PropTypes.bool,
	alertDateTime: PropTypes.any,
	hasControlFeature: PropTypes.bool,
	defaultToRecorded: PropTypes.bool,
	playbackStartOffset: PropTypes.number,
	initialRecordedDuration: PropTypes.string,
	initialRecordedTimeUnit: PropTypes.string,
	registerRecordedNavigator: PropTypes.func,
	selectedClip: PropTypes.object,
	onRecordedTimeframeChange: PropTypes.func
};

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const VideoBottomBar = ({
	selectedVideoMode,
	camera,
	contextId,
	playbackStreamName,
	instanceId,
	inDock,
	isOnAlertProfile,
	alertDateTime,
	hasControlFeature,
	dialogContainerRef,
	defaultToRecorded,
	playbackStartOffset,
	initialRecordedDuration,
	initialRecordedTimeUnit,
	registerRecordedNavigator,
	selectedClip,
	onRecordedTimeframeChange
}) => {
	const dir = useSelector(state => getDir(state));
	const canControl = useSelector(state => checkUserIntPermissionByFeedId(state)(camera?.feedId, "control"));
	const [toggleBottomActions, setToggleBottomActions] = useState(hasControlFeature && canControl); // actions available by default if applicable
	const dispatch = useDispatch();
	const styles = useMemo(() => {
		return {
			bottomChevronWrapper: {
				display: "flex",
				justifyContent: "center",
				alignItems: "center"
			},
			bottomChevron: {
				color: "#FFF",
				padding: 0,
				boxShadow: "0px 0px 3px #00000026",
				width: 20,
				height: 28,
				borderRadius: 4,
				opacity: 1,
				backdropFilter: "blur(3px)",
				backgroundColor: "#1D1D1F"
			}
		};
	}, []);

	const mainMenuShowControls = useSelector(
		makeSelectComponentProp(COMPONENT_KEY, instanceId, "components.mainMenu.showControls")
	);

	useEffect(() => {
		setToggleBottomActions(mainMenuShowControls);
	}, [mainMenuShowControls]);

	return (
		<div dir={dir}>
			{selectedVideoMode === "Live" ? (
				<Fragment>
					<div
						className="bccVideoBottomBarChevron"
						style={{ ...(dir === "ltr" && { left: 5 }), ...(dir === "rtl" && { right: 5 }), zIndex: 1901 }} data-testid="orion-components-cbcomponents-new-video-player-components-video-bottom-bar-div"
					>
						{hasControlFeature && canControl && (
							<div style={styles.bottomChevronWrapper}>
								<IconButton
									style={styles.bottomChevron}
									onClick={() => {
										setToggleBottomActions(!toggleBottomActions);
										dispatch(
											updateComponentInstance(COMPONENT_KEY, instanceId, {
												components: { mainMenu: { showControls: !toggleBottomActions } }
											})
										);
									}} data-testid="orion-components-cbcomponents-new-video-player-components-video-bottom-bar-icon-button"
								>
									{dir === "ltr" ? (
										<ChevronRightIcon sx={{ width: 15, height: 15 }} />
									) : (
										<ChevronLeftIcon sx={{ width: 15, height: 15 }} />
									)}
								</IconButton>
							</div>
						)}
					</div>
					{toggleBottomActions && hasControlFeature && (
						<LiveBottombar camera={camera} contextId={contextId} instanceId={instanceId} inDock={inDock} />
					)}
				</Fragment>
			) : (
				<RecordedBottomBar
					cameraId={camera.id}
					feedId={camera.feedId}
					instanceId={instanceId}
					playbackStreamName={playbackStreamName}
					isOnAlertProfile={isOnAlertProfile}
					alertDateTime={alertDateTime}
					defaultToRecorded={defaultToRecorded}
					dialogContainerRef={dialogContainerRef}
					playbackStartOffset={playbackStartOffset}
					initialRecordedDuration={initialRecordedDuration}
					initialRecordedTimeUnit={initialRecordedTimeUnit}
					registerRecordedNavigator={registerRecordedNavigator}
					playerType={camera.player.replayType || camera.player.type}
					selectedClip={selectedClip}
					onRecordedTimeframeChange={onRecordedTimeframeChange} data-testid="orion-components-cbcomponents-new-video-player-components-video-bottom-bar-recorded-bottom-bar"
				/>
			)}
		</div>
	);
};

VideoBottomBar.propTypes = propTypes;

export default VideoBottomBar;
